import { useRevisionStudy } from "./use-study.ts";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import {
  BlogPage,
  BlogArticle,
  FeedbackItem,
  SearchResult,
  ShareLink,
  type ShareInput,
} from "@ca/shared";
import {
  Button,
  Card,
  Chip,
  Fish,
  ErrorState,
  Modal,
  PageHead,
  Select,
  Skeleton,
  SpaceTag,
  Tabs,
} from "@ca/ui";
import { Pages } from "./revision-ui.tsx";
import { api } from "./api.ts";
import { uuidv7 } from "./queue.ts";
import { allPages } from "./navigation.ts";
const origin: string = import.meta.env.VITE_API_ORIGIN ?? "http://localhost:53000";
type Props = { uid: string; notify: (text: string) => void };
const errorText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const blogMeta = (p: {
  author: string | null;
  publishedAt: string | null;
  readTime: string | null;
}) =>
  [
    p.author,
    p.publishedAt ? new Date(p.publishedAt).toLocaleDateString("vi-VN") : "",
    p.readTime ? `${p.readTime} đọc` : "",
  ]
    .filter(Boolean)
    .join(" · ");
/** Blog — `S.blog` của bản mẫu (spec 19). Trang đọc bài là hộp lớn (chỉ staging có, giữ theo S12). */
export function Blog({ uid, notify }: Props) {
  const [tag, setTag] = useState(""),
    [offset, setOffset] = useState(0),
    [slug, setSlug] = useState<string | null>(
      /^\/blog\/([^/]+)$/.exec(window.location.pathname)?.[1] ?? null,
    ),
    [email, setEmail] = useState(""),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  const list = useQuery({
    queryKey: ["blog", tag, offset],
    queryFn: async () =>
      BlogPage.parse(
        await api(`/v1/blog?offset=${offset}${tag ? `&tag=${encodeURIComponent(tag)}` : ""}`),
      ),
  });
  const article = useQuery({
    queryKey: ["blog", slug],
    enabled: !!slug,
    queryFn: async () => BlogArticle.parse(await api(`/v1/blog/${encodeURIComponent(slug ?? "")}`)),
  });
  useRevisionStudy(uid, "blog", slug ? article.data?.id : undefined, "other");
  async function subscribe() {
    setBusy(true);
    try {
      const r = await fetch(`${origin}/v1/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!r.ok) throw new Error("Chưa đăng ký được. Kiểm tra email hoặc thử lại sau.");
      setMessage("Kiểm tra email và bấm liên kết xác nhận để nhận bản tin mỗi tuần.");
    } catch (e) {
      notify(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  const items = list.data?.items ?? [];
  const featured = offset === 0 ? (items.find((p) => p.isFeatured) ?? items[0]) : undefined;
  const rest = items.filter((p) => p !== featured);
  const save = (s: string) => window.dispatchEvent(new CustomEvent("ca-note-blog", { detail: s }));
  return (
    <>
      <PageHead
        space="common"
        title="Blog Cá Toeic"
        desc="Chuyện học thật của người thật, mẹo thi được kiểm chứng bằng dữ liệu, và hậu trường cách Cá chấm lỗi. Đọc 5 phút, dùng được ngay tối nay."
      >
        <Tabs
          label="Danh mục"
          items={[
            { key: "", label: "Tất cả" },
            ...(list.data?.tags ?? []).map((t) => ({ key: t, label: t })),
          ]}
          active={tag}
          onChange={(t) => {
            setTag(t);
            setOffset(0);
          }}
        />
      </PageHead>
      {list.isError ? (
        <ErrorState
          message="Chưa tải được bài viết."
          onRetry={() => {
            void list.refetch();
          }}
        />
      ) : list.isPending ? (
        <Skeleton />
      ) : (
        <>
          {featured && (
            <section className="mockcta" style={{ minHeight: 180 }}>
              <Chip tone="acc">⭐ Bài nổi bật · {featured.tag}</Chip>
              <h3 style={{ fontSize: 26, maxWidth: "22ch" }}>{featured.title}</h3>
              <p>{blogMeta(featured)}</p>
              <div className="row">
                <Button
                  variant="acc"
                  size="sm"
                  onClick={() => {
                    setSlug(featured.slug);
                  }}
                >
                  Đọc bài →
                </Button>
                <Button
                  size="sm"
                  style={{ background: "var(--side-2)", color: "var(--side-ink)" }}
                  onClick={() => {
                    save(featured.slug);
                  }}
                >
                  🔖 Lưu vào Take note
                </Button>
              </div>
            </section>
          )}
          {rest.length > 0 && (
            <div className="grid g3">
              {rest.map((p) => (
                <article
                  key={p.slug}
                  className="card stack note-like"
                  style={{ gap: 8 }}
                  role="button"
                  tabIndex={0}
                  aria-label={p.title}
                  onClick={() => {
                    setSlug(p.slug);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setSlug(p.slug);
                  }}
                >
                  <Chip style={{ alignSelf: "flex-start" }}>{p.tag}</Chip>
                  <b style={{ fontSize: 16 }}>{p.title}</b>
                  <span className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>
                    {blogMeta(p)}
                  </span>
                </article>
              ))}
            </div>
          )}
          {!items.length && (
            <section className="card faint">Chưa có bài viết trong nhóm này.</section>
          )}
          <Pages offset={offset} total={list.data.total} limit={12} set={setOffset} />
        </>
      )}
      <section className="card row between">
        <div>
          <h3>📬 Thư tuần của Cá</h3>
          <p className="muted" style={{ fontSize: 13.5 }}>
            Mỗi tuần: một mẹo thi, một collocation hay gặp, một câu bẫy mổ xẻ kỹ. Chỉ gửi sau khi
            bạn xác nhận trong hộp thư.
          </p>
          {message && (
            <p role="status" className="chip good" style={{ marginTop: 6 }}>
              {message}
            </p>
          )}
        </div>
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            void subscribe();
          }}
        >
          <input
            className="input"
            style={{ width: 240 }}
            type="email"
            required
            maxLength={254}
            placeholder="email của bạn"
            aria-label="Email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
          <Button disabled={busy} type="submit">
            Đăng ký
          </Button>
        </form>
      </section>
      {slug && (
        <Modal
          title={article.data?.title ?? "Bài viết"}
          wide
          onClose={() => {
            setSlug(null);
          }}
        >
          {article.isError ? (
            <p role="alert">Không tải được bài viết.</p>
          ) : article.isPending ? (
            <p className="faint">Đang tải…</p>
          ) : (
            <div className="stack">
              <div className="row">
                <SpaceTag space="common" />
                <Chip>{article.data.tag}</Chip>
                <span className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>
                  {blogMeta(article.data)}
                </span>
              </div>
              <article
                className="community-article"
                dangerouslySetInnerHTML={{ __html: article.data.bodyHtml }}
              />
              <div className="row">
                <Button
                  variant="acc"
                  onClick={() => {
                    save(slug);
                  }}
                >
                  🔖 Lưu vào Take note
                </Button>
                <span className="faint" style={{ fontSize: 12.5, fontWeight: 600 }}>
                  ⏺ Đang đọc bài: giờ học tính vào nhóm “Khác”.
                </span>
              </div>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
const FEEDBACK_TYPES = {
  idea: "Góp ý / Đề xuất",
  bug: "Báo lỗi",
  content: "Nội dung đề thi",
  other: "Khác",
} as const;
const FEEDBACK_STATUS: Record<string, [string, "good" | "warn" | "acc" | undefined]> = {
  new: ["Mới", "acc"],
  planned: ["Đã lên kế hoạch", "warn"],
  done: ["Hoàn thành", "good"],
  rejected: ["Chưa triển khai", undefined],
};
/** Góp ý — `S.feedback` của bản mẫu (spec 20) + danh sách góp ý công khai và bình chọn (S12). */
export function Feedback({ uid, notify }: Props) {
  const [type, setType] = useState<keyof typeof FEEDBACK_TYPES>("idea"),
    [message, setMessage] = useState(""),
    [contactEmail, setContactEmail] = useState(""),
    [offset, setOffset] = useState(0),
    [busy, setBusy] = useState(false),
    pending = useRef<{
      key: string;
      type: typeof type;
      message: string;
      contactEmail: string | null;
    } | null>(null),
    voteKeys = useRef(new Map<string, { key: string; voted: boolean }>());
  const list = useQuery({
    queryKey: ["feedback", uid, offset],
    queryFn: async () =>
      z.array(FeedbackItem).parse(await api(`/v1/feedback?offset=${offset}`, { accountId: uid })),
  });
  async function create() {
    setBusy(true);
    try {
      pending.current ??= {
        key: uuidv7(),
        type,
        message,
        contactEmail: contactEmail.trim() || null,
      };
      await api("/v1/feedback", {
        method: "POST",
        key: pending.current.key,
        body: {
          type: pending.current.type,
          message: pending.current.message,
          contactEmail: pending.current.contactEmail,
        },
        accountId: uid,
      });
      pending.current = null;
      setMessage("");
      setContactEmail("");
      await list.refetch();
      notify("Đã gửi góp ý.");
    } catch (e) {
      notify(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  async function vote(id: string, voted: boolean) {
    setBusy(true);
    try {
      const intent = voteKeys.current.get(id) ?? { key: uuidv7(), voted };
      voteKeys.current.set(id, intent);
      await api(`/v1/feedback/${id}/vote`, {
        method: "PUT",
        body: { voted: intent.voted },
        key: intent.key,
        accountId: uid,
      });
      voteKeys.current.delete(id);
      await list.refetch();
    } catch (e) {
      notify(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  const locked = busy || !!pending.current;
  return (
    <>
      <PageHead
        space="common"
        title="💬 Góp ý"
        desc="Phản ánh trực tiếp tới đội ngũ Cá Toeic: lỗi, góp ý hoặc đề xuất tính năng. Góp ý hiển thị cho cộng đồng, đừng đưa thông tin riêng tư vào nội dung."
      />
      <form
        className="card stack"
        style={{ maxWidth: 640 }}
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <div className="field">
          <label>Loại phản hồi</label>
          <Select
            label="Loại"
            value={type}
            disabled={locked}
            options={Object.entries(FEEDBACK_TYPES).map(([value, label]) => ({
              value: value as keyof typeof FEEDBACK_TYPES,
              label,
            }))}
            onChange={setType}
          />
        </div>
        <div className="field">
          <label htmlFor="fbMsg">Nội dung</label>
          <textarea
            id="fbMsg"
            className="input"
            rows={5}
            required
            minLength={10}
            maxLength={2000}
            disabled={locked}
            placeholder="Bạn muốn phản ánh điều gì?"
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
            }}
          />
        </div>
        <div className="field">
          <label htmlFor="fbEmail">Email liên hệ (tuỳ chọn)</label>
          <input
            id="fbEmail"
            className="input"
            type="email"
            maxLength={254}
            disabled={locked}
            placeholder="email của bạn · chỉ đội ngũ quản trị thấy"
            value={contactEmail}
            onChange={(e) => {
              setContactEmail(e.target.value);
            }}
          />
        </div>
        <Button
          variant="acc"
          style={{ alignSelf: "flex-start" }}
          disabled={busy || message.trim().length < 10}
          type="submit"
        >
          {pending.current ? "Gửi lại" : "Gửi phản hồi"}
        </Button>
      </form>
      <Card>
        <div className="card-head">
          <div>
            <h2>Góp ý của cộng đồng</h2>
            <p className="sub">
              Bình chọn những góp ý bạn cũng muốn có, đội ngũ ưu tiên theo số phiếu.
            </p>
          </div>
        </div>
        {list.isError ? (
          <ErrorState
            message="Chưa tải được góp ý."
            onRetry={() => {
              void list.refetch();
            }}
          />
        ) : list.isPending ? (
          <p className="faint">Đang tải…</p>
        ) : list.data.length ? (
          list.data.map((item) => {
            const [status, tone] = FEEDBACK_STATUS[item.status] ?? [item.status, undefined];
            return (
              <div key={item.id} className="list-row" style={{ alignItems: "flex-start" }}>
                <div className="stack grow" style={{ gap: 6 }}>
                  <div className="row" style={{ gap: 6 }}>
                    <Chip>
                      {(FEEDBACK_TYPES as Record<string, string>)[item.type] ?? item.type}
                    </Chip>
                    <Chip tone={tone}>{status}</Chip>
                  </div>
                  <p className="community-message">{item.message}</p>
                </div>
                <Button
                  disabled={busy}
                  size="sm"
                  variant={item.voted ? "dark" : "soft"}
                  onClick={() => {
                    void vote(item.id, !item.voted);
                  }}
                >
                  {item.voted ? "Bỏ bình chọn" : "Bình chọn"} · {item.votes}
                </Button>
              </div>
            );
          })
        ) : (
          <p className="faint">Chưa có góp ý nào. Hãy là người đầu tiên.</p>
        )}
        {(offset > 0 || (list.data?.length ?? 0) >= 30) && (
          <div className="row" style={{ justifyContent: "center", marginTop: 10 }}>
            <Button
              variant="soft"
              size="sm"
              disabled={!offset}
              onClick={() => {
                setOffset(Math.max(0, offset - 30));
              }}
            >
              ‹ Trước
            </Button>
            <Button
              variant="soft"
              size="sm"
              disabled={(list.data?.length ?? 0) < 30}
              onClick={() => {
                setOffset(offset + 30);
              }}
            >
              Sau ›
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}
/** Ô tìm trên thanh trên cùng (`.search` của bản mẫu): phím `/` để tìm, kết quả thả xuống. */
export function QuickSearch({ uid }: Pick<Props, "uid">) {
  const [input, setInput] = useState(""),
    [q, setQ] = useState(""),
    [open, setOpen] = useState(false),
    box = useRef<HTMLDivElement>(null),
    field = useRef<HTMLInputElement>(null),
    navigate = useNavigate();
  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(input.trim());
    }, 250);
    return () => {
      clearTimeout(timer);
    };
  }, [input]);
  useEffect(() => {
    const shortcut = (e: KeyboardEvent) => {
      const typing =
        e.target instanceof HTMLElement &&
        (e.target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName));
      if (
        ((e.key === "/" && !typing) || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k")) &&
        field.current?.offsetParent
      ) {
        e.preventDefault();
        field.current.focus();
      }
    };
    const outside = (e: PointerEvent) => {
      if (!(e.target instanceof Node) || !box.current?.contains(e.target)) setOpen(false);
    };
    window.addEventListener("keydown", shortcut);
    window.addEventListener("pointerdown", outside);
    return () => {
      window.removeEventListener("keydown", shortcut);
      window.removeEventListener("pointerdown", outside);
    };
  }, []);
  const results = useQuery({
    queryKey: ["quick-search", uid, q],
    enabled: q.length >= 2,
    queryFn: async () =>
      z
        .array(SearchResult)
        .parse(await api(`/v1/search?q=${encodeURIComponent(q)}`, { accountId: uid })),
    staleTime: 0,
    gcTime: 0,
  });
  const lower = q.toLowerCase();
  const pages = lower ? allPages().filter((p) => p.label.toLowerCase().includes(lower)) : [];
  const go = (path: string) => {
    setOpen(false);
    setInput("");
    void navigate(path === "/" ? { to: "/" } : { to: "/$", params: { _splat: path.slice(1) } });
  };
  const items = [
    ...(results.data ?? []).map((r) => ({
      key: `${r.kind}:${r.id}`,
      title: r.title,
      kind: { test: "Đề thi", vocab: "Từ vựng", note: "Take note" }[r.kind],
      path: r.path,
    })),
    ...pages.map((p) => ({ key: `page:${p.path}`, title: p.label, kind: "Trang", path: p.path })),
  ].slice(0, 7);
  return (
    <div className="search" role="search" ref={box}>
      <span className="ico" aria-hidden="true">
        ⌕
      </span>
      <input
        ref={field}
        type="search"
        placeholder="Tìm đề, từ vựng, ghi chú…"
        autoComplete="off"
        aria-label="Tìm nhanh"
        maxLength={100}
        value={input}
        onFocus={() => {
          setOpen(true);
        }}
        onChange={(e) => {
          setInput(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
            e.currentTarget.blur();
          }
          if (e.key === "Enter" && items[0]) go(items[0].path);
        }}
      />
      <kbd>/</kbd>
      {open && q.length >= 2 && (
        <div className="search-results">
          {results.isError ? (
            <span className="faint" style={{ padding: "8px 10px", fontSize: 13 }} role="alert">
              Chưa tìm được.{" "}
              <button
                className="btn btn-soft btn-sm"
                onClick={() => {
                  void results.refetch();
                }}
              >
                Thử lại
              </button>
            </span>
          ) : results.isPending && !items.length ? (
            <span className="faint" style={{ padding: "8px 10px", fontSize: 13 }}>
              Đang tìm…
            </span>
          ) : items.length ? (
            items.map((i) => (
              <button
                key={i.key}
                onClick={() => {
                  go(i.path);
                }}
              >
                {i.title}
                <small>{i.kind}</small>
              </button>
            ))
          ) : (
            <span className="faint" style={{ padding: "8px 10px", fontSize: 13 }}>
              Không thấy “{q}”.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
export function ShareButton({ uid, notify, input }: Props & { input: z.infer<typeof ShareInput> }) {
  const [url, setUrl] = useState<string | null>(null),
    [busy, setBusy] = useState(false),
    [copied, setCopied] = useState(false),
    pending = useRef<string | null>(null);
  async function generate() {
    setBusy(true);
    try {
      pending.current ??= uuidv7();
      const link = ShareLink.parse(
        await api("/v1/me/share", {
          method: "POST",
          body: input,
          key: pending.current,
          accountId: uid,
        }),
      );
      pending.current = null;
      setUrl(link.url);
      setCopied(false);
    } catch (e) {
      notify(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Button
        variant="soft"
        disabled={busy}
        onClick={() => {
          void generate();
        }}
      >
        📸 Chia sẻ ảnh
      </Button>
      {url && (
        <Modal
          title="Ảnh chia sẻ"
          onClose={() => {
            setUrl(null);
          }}
        >
          <div className="stack">
            <p className="muted">
              Ảnh công khai chứa số liệu tổng hợp. Bất kỳ ai có liên kết đều xem được.
            </p>
            <img
              src={url}
              alt="Ảnh chia sẻ điểm hoặc tổng kết ngày học"
              style={{ width: "100%", borderRadius: 18, background: "var(--surface-2)" }}
            />
            <div className="row">
              <a className="btn btn-soft btn-sm" href={url} target="_blank" rel="noreferrer">
                Mở / lưu ảnh PNG
              </a>
              <Button
                variant="acc"
                size="sm"
                onClick={() => {
                  void navigator.clipboard
                    .writeText(url)
                    .then(() => {
                      setCopied(true);
                    })
                    .catch(() => {
                      notify("Hãy sao chép liên kết bên dưới.");
                    });
                }}
              >
                {copied ? "Đã sao chép" : "Sao chép liên kết"}
              </Button>
              <Button
                variant="line"
                size="sm"
                onClick={() => {
                  void api(`/v1/me/share/${url.split("/").at(-1)?.replace(".png", "") ?? ""}`, {
                    method: "DELETE",
                    accountId: uid,
                  })
                    .then(() => {
                      setUrl(null);
                      notify("Đã thu hồi trên máy chủ. Ảnh đã tải hoặc cache vẫn có thể còn.");
                    })
                    .catch((e: unknown) => {
                      notify(errorText(e));
                    });
                }}
              >
                Thu hồi
              </Button>
            </div>
            <input className="input" readOnly aria-label="Liên kết ảnh" value={url} />
          </div>
        </Modal>
      )}
    </>
  );
}
export function NewsletterConfirmation() {
  const token = new URLSearchParams(window.location.search).get("token") ?? "",
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  async function submit(action: "confirm" | "unsubscribe") {
    setBusy(true);
    try {
      const r = await fetch(`${origin}/v1/newsletter/${action}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      if (!r.ok) throw new Error("Liên kết không hợp lệ, hết hạn hoặc đã được sử dụng.");
      setMessage(
        action === "confirm" ? "Đã xác nhận. Bạn sẽ nhận bản tin mỗi tuần." : "Đã hủy đăng ký.",
      );
      history.replaceState(null, "", "/newsletter");
    } catch (e) {
      setMessage(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="gate">
      <div className="modal stack" style={{ gap: 14 }}>
        <div style={{ width: 110, margin: "0 auto" }}>
          <Fish />
        </div>
        <h1 style={{ textAlign: "center", fontSize: 26 }}>📬 Thư tuần của Cá</h1>
        {message ? (
          <p role="status" className="muted" style={{ textAlign: "center" }}>
            {message}
          </p>
        ) : (
          <>
            <p className="muted" style={{ textAlign: "center" }}>
              Bấm xác nhận để đăng ký nhận bản tin mỗi tuần.
            </p>
            <div className="row" style={{ justifyContent: "center" }}>
              <Button
                variant="acc"
                disabled={busy || !token}
                onClick={() => {
                  void submit("confirm");
                }}
              >
                Xác nhận đăng ký
              </Button>
              <Button
                variant="line"
                disabled={busy || !token}
                onClick={() => {
                  void submit("unsubscribe");
                }}
              >
                Hủy đăng ký
              </Button>
            </div>
          </>
        )}
        <a className="btn btn-soft" href="/">
          Về ứng dụng
        </a>
      </div>
    </main>
  );
}
